import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { storyDB } from './db'

interface Profile {
  id: string
  name: string
  age: number
  interests: string[]
  sensitivities: string[]
  avatarUrl?: string
}

interface Story {
  id: string
  title: string
  content: string
  audioUrl?: string
  imageUrl?: string
  profileId: string
  createdAt: Date
  isFavorite: boolean
  rating?: number
  totalChapters: number
  completedChapters: number
  isCompleted: boolean
  lastReadAt?: Date
  // New (MVP library)
  realm?: 'stories' | 'explore' | 'logic' | 'creativity'
  status?: 'generating' | 'ready' | 'failed'
  coverUrl?: string
  authorName?: string
}

interface AppState {
  // Theme
  theme: 'light' | 'dark'
  setTheme: (theme: 'light' | 'dark') => void

  // Profiles
  profiles: Profile[]
  currentProfile: Profile | null
  setProfiles: (profiles: Profile[]) => void
  setCurrentProfile: (profile: Profile | null) => void
  addProfile: (profile: Profile) => void
  updateProfile: (id: string, updates: Partial<Profile>) => void
  deleteProfile: (id: string) => void

  // Stories
  stories: Story[]
  currentStory: Story | null
  setStories: (stories: Story[]) => void
  setCurrentStory: (story: Story | null) => void
  addStory: (story: Story) => void
  updateStory: (id: string, updates: Partial<Story>) => void
  deleteStory: (id: string) => void

  // App state
  isLoading: boolean
  error: string | null
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void

  // Settings
  settings: {
    autoPlay: boolean
    textSize: 'small' | 'medium' | 'large'
    readingSpeed: 'slow' | 'normal' | 'fast'
    soundEffects: boolean
    hapticFeedback: boolean
  }
  updateSettings: (settings: Partial<AppState['settings']>) => void

  // Sync with IndexedDB
  syncWithDB: () => Promise<void>
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Theme
      theme: 'light',
      setTheme: (theme) => set({ theme }),

      // Profiles
      profiles: [],
      currentProfile: null,
      setProfiles: (profiles) => set({ profiles }),
      setCurrentProfile: (profile) => set({ currentProfile: profile }),
      addProfile: async (profile) => {
        set((state) => ({ profiles: [...state.profiles, profile] }))
        await storyDB.saveProfile({
          ...profile,
          createdAt: new Date(),
        })
      },
      updateProfile: async (id, updates) => {
        set((state) => ({
          profiles: state.profiles.map((p) =>
            p.id === id ? { ...p, ...updates } : p
          ),
          currentProfile:
            state.currentProfile?.id === id
              ? { ...state.currentProfile, ...updates }
              : state.currentProfile,
        }))
        const profile = get().profiles.find((p) => p.id === id)
        if (profile) {
          await storyDB.saveProfile({
            ...profile,
            ...updates,
            createdAt: new Date(),
          })
        }
      },
      deleteProfile: async (id) => {
        set((state) => ({
          profiles: state.profiles.filter((p) => p.id !== id),
          currentProfile:
            state.currentProfile?.id === id ? null : state.currentProfile,
          stories: state.stories.filter((s) => s.profileId !== id),
        }))
        await storyDB.deleteProfile(id)
      },

      // Stories
      stories: [],
      currentStory: null,
      setStories: (stories) => set({ stories }),
      setCurrentStory: (story) => set({ currentStory: story }),
      addStory: async (story) => {
        set((state) => ({ stories: [...state.stories, story] }))
        await storyDB.saveStory({
          ...story,
          tags: [],
          totalChapters: story.totalChapters || 1,
          completedChapters: story.completedChapters || 0,
          isCompleted: story.isCompleted || false,
        })
      },
      updateStory: async (id, updates) => {
        set((state) => ({
          stories: state.stories.map((s) =>
            s.id === id ? { ...s, ...updates } : s
          ),
          currentStory:
            state.currentStory?.id === id
              ? { ...state.currentStory, ...updates }
              : state.currentStory,
        }))
        const story = get().stories.find((s) => s.id === id)
        if (story) {
          await storyDB.saveStory({
            ...story,
            ...updates,
            tags: [],
            totalChapters: story.totalChapters || 1,
            completedChapters: story.completedChapters || 0,
            isCompleted: story.isCompleted || false,
          })
        }
      },
      deleteStory: async (id) => {
        set((state) => ({
          stories: state.stories.filter((s) => s.id !== id),
          currentStory:
            state.currentStory?.id === id ? null : state.currentStory,
        }))
        await storyDB.deleteStory(id)
      },

      // App state
      isLoading: false,
      error: null,
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      // Settings
      settings: {
        autoPlay: true,
        textSize: 'medium',
        readingSpeed: 'normal',
        soundEffects: true,
        hapticFeedback: true,
      },
      updateSettings: (settings) =>
        set((state) => ({
          settings: { ...state.settings, ...settings },
        })),

      // Sync with IndexedDB
      syncWithDB: async () => {
        const profiles = await storyDB.getAllProfiles()
        const stories = await storyDB.getAllStories()
        
        set({
          profiles: profiles.map(p => ({
            id: p.id,
            name: p.name,
            age: p.age,
            interests: p.interests,
            sensitivities: p.sensitivities,
            avatarUrl: p.avatarUrl,
          })),
          stories: stories.map(s => ({
            id: s.id,
            title: s.title,
            content: s.content,
            audioUrl: s.audioUrl,
            imageUrl: s.imageUrl,
            profileId: s.profileId,
            createdAt: s.createdAt,
            isFavorite: s.isFavorite,
            rating: s.rating,
            totalChapters: s.totalChapters || 1,
            completedChapters: s.completedChapters || 0,
            isCompleted: s.isCompleted || false,
            lastReadAt: s.lastReadAt,
          })),
        })
      },
    }),
    {
      name: 'story-tales-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        theme: state.theme,
        currentProfile: state.currentProfile,
        settings: state.settings,
      }),
    }
  )
)
